import React, { useEffect, useState } from 'react';
import { api } from './api';
import { t } from './i18n';

const COLORS = {
  primary: '#1a4d8f',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  accent: '#d4af37',
  bg: '#f7f8fa',
};

const TYPE_ICON = {
  breakfast: '🥐',
  lunch: '🥗',
  dinner: '🍽️',
  shabbat: '🕯️',
  drinks: '🍷',
  wine: '🍷',
  tasting: '⭐',
  kids: '🧒',
  catering: '🎁',
  other: '📋',
};

function MenuBody({ parsed, raw, source_url, source_type }) {
  const hasStructured = parsed && Array.isArray(parsed.sections) && parsed.sections.length > 0;

  return (
    <div>
      {hasStructured ? (
        <div>
          {parsed.sections.map((section, sIdx) => (
            <div key={sIdx} style={{ marginBottom: sIdx < parsed.sections.length - 1 ? 14 : 2 }}>
              {section.title && (
                <h4 style={{
                  margin: '0 0 6px 0',
                  fontSize: 13,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: COLORS.primary,
                  borderBottom: `1.5px solid ${COLORS.accent}`,
                  paddingBottom: 3,
                  display: 'inline-block',
                }}>
                  {section.title}
                </h4>
              )}
              <div className="jc-menu-items">
                {section.items.map((item, iIdx) => (
                  <div
                    key={iIdx}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 10,
                      padding: '3px 0',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: COLORS.text }}>
                      <span style={{ fontWeight: 600 }}>{item.name}</span>
                      {item.description && (
                        <span style={{ color: COLORS.muted, marginLeft: 6 }}>· {item.description}</span>
                      )}
                    </div>
                    {item.price && (
                      <div style={{
                        fontWeight: 700,
                        color: COLORS.primary,
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>{item.price}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 14, lineHeight: 1.6, color: COLORS.text, whiteSpace: 'pre-wrap' }}>
          {raw}
        </div>
      )}

      {source_url && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
          <a
            href={source_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: COLORS.primary, textDecoration: 'none' }}
          >
            {source_type === 'pdf' ? t('📄 View original PDF') : t('🖼️ View original menu image')}
          </a>
        </div>
      )}
    </div>
  );
}

export default function Menu({ establishment }) {
  const [menus, setMenus] = useState(null); // null = loading, [] = no multi-menus
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!establishment?.id) return;
    api.listMenus(establishment.id)
      .then((r) => setMenus(r.menus || []))
      .catch(() => setMenus([]));
  }, [establishment?.id]);

  const legacyMenu = establishment.menu || establishment.menu_parsed;

  // Nothing to show
  if (menus === null) return null; // still loading
  if (menus.length === 0 && !legacyMenu) return null;

  // No structured menus but has legacy single menu — render that
  if (menus.length === 0 && legacyMenu) {
    return (
      <div style={{ background: 'white', padding: 16, borderRadius: 12, marginTop: 16, border: `1px solid ${COLORS.border}` }}>
        <h3 style={{ marginTop: 0, color: COLORS.primary, display: 'flex', alignItems: 'center', gap: 8 }}>
          {t('🍽️ Menu')}
        </h3>
        <MenuBody parsed={establishment.menu_parsed} raw={establishment.menu} />
      </div>
    );
  }

  // Multi-menu — show tabs (or no tabs if only one)
  const active = menus[Math.min(activeIdx, menus.length - 1)] || menus[0];

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 12, marginTop: 16, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ margin: '0 0 10px', color: COLORS.primary, display: 'flex', alignItems: 'center', gap: 6, fontSize: 16 }}>
        {t('🍽️ Menu')}{menus.length > 1 ? 's' : ''}
      </h3>

      {menus.length > 1 && (
        <div
          role="tablist"
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 10,
            paddingBottom: 6,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          {menus.map((m, i) => {
            const isActive = i === activeIdx;
            const icon = TYPE_ICON[m.menu_type] || '📋';
            return (
              <button
                key={m.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveIdx(i)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: `1px solid ${isActive ? COLORS.primary : COLORS.border}`,
                  background: isActive ? COLORS.primary : 'white',
                  color: isActive ? 'white' : COLORS.text,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <span>{icon}</span>
                {m.name}
              </button>
            );
          })}
        </div>
      )}

      <MenuBody
        parsed={active.parsed}
        raw={active.raw_text}
        source_url={active.source_url}
        source_type={active.source_type}
      />
    </div>
  );
}
